import { FILE_TIMEOUT_MS, baseApi, transportRequest } from '@krp/client-runtime'
import { withImageType } from '@krp/ui-kit'

import { decodePhotoPreparation } from '../../common-photos/api/contracts'
import type { CropBox, PhotoPreparation } from '../../common-photos/model/types'

import { decodePhotoSources, decodeRegistry, decodeSendJob, decodeSendResult, decodeStaging, decodeStagingList } from './contracts'
import { PHOTO_REGISTRY_URLS } from './urls'
import type { PhotoRegistry, PhotoSendResult, PhotoSource, PhotoStaging, SendJob } from '../model/types'

/**
 * Реестр фотографий общего товара и отправка фото в источники в общем `baseApi`. Реестр — read-model на сервере: клиент его читает и принимает
 * решения человека. Отправку выполняет служба синхронизации: команда возвращает задачу, ход читается запросом `platformPhotoSendJob`.
 * «Добавить фото» идёт мимо кэша RTK (`File` и `Blob` в Redux класть нельзя) тремя вызовами: `prepareStagingPhoto`, `previewStagingPhoto`, `saveStagingPhoto`.
 */
export const PLATFORM_PHOTO_REGISTRY_TAG = 'PlatformPhotoRegistry' as const

export interface PhotoRegistryArgs {
  productId: string
}

export interface PhotoDecisionArgs {
  productId: string
  keyA: string
  keyB: string
  decision: 'same' | 'different'
}

/** Отправка: ровно одно из `photoKey` (фотография реестра) и `stagingId` (временная загрузка). Ключ операции — на одно намерение человека. */
export interface PhotoSendArgs {
  productId: string
  photoKey?: string
  stagingId?: string
  resourceId: string
  operationId: string
  replaceExisting: boolean
}

/** Удаление временной загрузки: ключа операции нет, повтор безопасен (загрузки уже нет — 404 словами). */
export interface PhotoDeleteStagingArgs {
  productId: string
  stagingId: string
}

/** Удаление из источника: ключ операции — на одно намерение человека, повтор несёт тот же. */
export interface PhotoDeleteFromSourceArgs {
  productId: string
  photoKey: string
  resourceId: string
  operationId: string
}

export interface PhotoSendJobArgs {
  jobId: string
}

export const photoRegistryTag = (productId: string) => ({ type: PLATFORM_PHOTO_REGISTRY_TAG, id: productId })

export const photoRegistryApi = baseApi.enhanceEndpoints({ addTagTypes: [PLATFORM_PHOTO_REGISTRY_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    platformPhotoRegistry: build.query<PhotoRegistry, PhotoRegistryArgs>({
      query: ({ productId }) => ({ url: PHOTO_REGISTRY_URLS.registry(productId), decode: decodeRegistry }),
      providesTags: (_result, _error, { productId }) => [photoRegistryTag(productId)],
    }),
    /** Живые временные загрузки товара: после перезагрузки окна из них снова складываются плитки «Пока нигде не лежит». */
    platformPhotoStaging: build.query<readonly PhotoStaging[], PhotoRegistryArgs>({
      query: ({ productId }) => ({ url: PHOTO_REGISTRY_URLS.staging(productId), decode: decodeStagingList }),
      providesTags: (_result, _error, { productId }) => [photoRegistryTag(productId)],
    }),
    /** Источники фото товара одним ответом сервера: подписи, что умеет вид, пределы. Окно фото берёт список только отсюда. */
    platformPhotoSources: build.query<readonly PhotoSource[], PhotoRegistryArgs>({
      query: ({ productId }) => ({ url: PHOTO_REGISTRY_URLS.sources(productId), decode: decodePhotoSources }),
      providesTags: (_result, _error, { productId }) => [photoRegistryTag(productId)],
    }),
    platformPhotoDecision: build.mutation<PhotoRegistry, PhotoDecisionArgs>({
      query: ({ productId, keyA, keyB, decision }) => ({
        url: PHOTO_REGISTRY_URLS.decisions(productId),
        method: 'POST',
        body: { keyA, keyB, decision },
        decode: decodeRegistry,
      }),
      invalidatesTags: (_result, _error, { productId }) => [photoRegistryTag(productId)],
    }),
    platformPhotoSend: build.mutation<PhotoSendResult, PhotoSendArgs>({
      query: ({ productId, photoKey, stagingId, resourceId, operationId, replaceExisting }) => ({
        url:
          stagingId !== undefined
            ? PHOTO_REGISTRY_URLS.sendStaging(productId, stagingId)
            : PHOTO_REGISTRY_URLS.sendIdentity(productId, photoKey ?? ''),
        method: 'POST',
        body: { operationId, resourceId, replaceExisting },
        decode: decodeSendResult,
      }),
    }),
    platformPhotoDeleteStaging: build.mutation<null, PhotoDeleteStagingArgs>({
      query: ({ productId, stagingId }) => ({ url: PHOTO_REGISTRY_URLS.deleteStaging(productId, stagingId), method: 'DELETE', decode: () => null }),
    }),
    platformPhotoDeleteFromSource: build.mutation<PhotoSendResult, PhotoDeleteFromSourceArgs>({
      query: ({ productId, photoKey, resourceId, operationId }) => ({
        url: PHOTO_REGISTRY_URLS.deleteIdentity(productId, photoKey),
        method: 'POST',
        body: { operationId, resourceId },
        decode: decodeSendResult,
      }),
    }),
    platformPhotoSendJob: build.query<SendJob, PhotoSendJobArgs>({
      query: ({ jobId }) => ({ url: PHOTO_REGISTRY_URLS.job(jobId), decode: decodeSendJob }),
    }),
  }),
})

export const {
  platformPhotoRegistry,
  platformPhotoSources,
  platformPhotoStaging,
  platformPhotoDecision,
  platformPhotoSend,
  platformPhotoDeleteStaging,
  platformPhotoDeleteFromSource,
  platformPhotoSendJob,
} = photoRegistryApi.endpoints

/**
 * «Добавить фото», шаг 1: файл уходит на подготовку, возвращается вырез без фона и предложенная рамка. Сервер ничего не сохраняет, ключа операции нет,
 * повтор безопасен. Прогресс — реальные байты загрузки.
 */
export function prepareStagingPhoto(
  productId: string,
  file: File,
  signal: AbortSignal,
  onProgress: (loaded: number, total: number | null) => void,
) {
  const formData = new FormData()
  const typed = withImageType(file)
  formData.append('file', typed, typed.name)
  return transportRequest<PhotoPreparation>(
    { url: PHOTO_REGISTRY_URLS.stagingPrepare(productId), method: 'POST', formData, timeoutMs: FILE_TIMEOUT_MS, decode: decodePhotoPreparation },
    signal,
    { onUploadProgress: onProgress },
  )
}

function appendBox(formData: FormData, box: CropBox): void {
  formData.append('left', String(box.left))
  formData.append('top', String(box.top))
  formData.append('right', String(box.right))
  formData.append('bottom', String(box.bottom))
}

/** Расширение выреза по типу из ответа сервера: тип берётся у самого Blob, а не подставляется. */
function cutoutName(cutout: Blob): string {
  const extension = cutout.type.startsWith('image/') ? cutout.type.slice('image/'.length) : 'bin'
  return `cutout.${extension === 'jpeg' ? 'jpg' : extension}`
}

/** Шаг 2: вырез и рамка человека → кадр, каким он получится (изображение). Ничего не сохраняется, повтор безопасен. */
export function previewStagingPhoto(productId: string, cutout: Blob, box: CropBox, signal: AbortSignal) {
  const formData = new FormData()
  formData.append('file', cutout, cutoutName(cutout))
  appendBox(formData, box)
  return transportRequest<Blob>(
    { url: PHOTO_REGISTRY_URLS.stagingPreview(productId), method: 'POST', formData, responseType: 'blob', timeoutMs: FILE_TIMEOUT_MS },
    signal,
  )
}

/**
 * Шаг 3: «Сохранить» — вырез, исходник как выбран, ключ операции и рамка. Кадр ложится во временную загрузку и никуда не отправляется. Повтора transport не
 * делает: обрыв — «исход неизвестен», а повтор с тем же `operationId` сервер узнаёт и второй загрузки не создаёт.
 */
export function saveStagingPhoto(productId: string, cutout: Blob, original: File, box: CropBox, operationId: string, signal: AbortSignal) {
  const formData = new FormData()
  formData.append('file', cutout, cutoutName(cutout))
  const typed = withImageType(original)
  formData.append('original', typed, typed.name)
  formData.append('operationId', operationId)
  appendBox(formData, box)
  return transportRequest<PhotoStaging>(
    { url: PHOTO_REGISTRY_URLS.staging(productId), method: 'POST', formData, timeoutMs: FILE_TIMEOUT_MS, decode: decodeStaging },
    signal,
  )
}
